import * as z from 'zod';
import { type FactionSpawnData, type SpawnBracket, SpawnBracketSchema } from './content';

/**
 * Custom spawn-bracket schemas (contracts §5).
 *
 * A custom bracket is a user-authored spawn table built from a declared model pool. It reuses the published
 * spawn-table shape from `./content` so a custom table is interchangeable with a bundled faction one everywhere
 * downstream; only its authoring, density validation, and index projection live here.
 */

/**
 * The durable, user-authored part of a pool entry. Names, rules and other 40k content deliberately do not belong
 * here: they are hydrated from the current content release whenever a bracket is opened.
 */
export const CustomSpawnBracketPoolEntrySchema = z
    .object({
        count: z.number().int().min(0),
        datasheetId: z.string().min(1),
    })
    .strict();
export type CustomSpawnBracketPoolEntry = z.infer<typeof CustomSpawnBracketPoolEntrySchema>;

export const CustomSpawnBracketValidationIssueSchema = z.object({
    actual: z.number().int().min(0),
    bracket: SpawnBracketSchema.optional(),
    code: z.enum(['empty-bracket', 'sparse-bracket']),
    message: z.string(),
    missing: z.number().int().min(0),
    required: z.number().int().min(0),
    severity: z.literal('error'),
});
export type CustomSpawnBracketValidationIssue = z.infer<typeof CustomSpawnBracketValidationIssueSchema>;

export const CustomSpawnBracketValidationSchema = z.object({
    algorithmVersion: z.literal(1),
    isValid: z.boolean(),
    issues: z.array(CustomSpawnBracketValidationIssueSchema),
    metrics: z.object({
        distinctSpawnOptions: z.number().int().min(0),
        optionsByBracket: z.object({
            '10+': z.number().int().min(0),
            '3-4': z.number().int().min(0),
            '5-6': z.number().int().min(0),
            '7-9': z.number().int().min(0),
        }),
        populatedBrackets: z.number().int().min(0).max(4),
        totalModels: z.number().int().min(0),
    }),
});
export type CustomSpawnBracketValidation = z.infer<typeof CustomSpawnBracketValidationSchema>;

const CUSTOM_SPAWN_MIN_OPTIONS_PER_BRACKET = 2;
const CUSTOM_SPAWN_BRACKETS: readonly SpawnBracket[] = ['3-4', '5-6', '7-9', '10+'];

/**
 * Validates a generated custom table against the useful-density floor of the bundled Poorhammer tables. 27 of 31
 * published tables provide at least two distinct outcomes in every roll bracket; the four exceptions are specialist
 * tables whose sparse/empty bands are the documented reason to mix factions. Requiring the same per-band floor for
 * a custom table prevents dead roll ranges without penalising elite armies by raw model count. `algorithmVersion`
 * keeps persisted results explicitly migratable if the benchmark changes.
 */
export function validateCustomSpawnBracket({
    pool,
    spawnData,
}: {
    pool: readonly CustomSpawnBracketPoolEntry[];
    spawnData: FactionSpawnData;
}): CustomSpawnBracketValidation {
    const signatures = new Set<string>();
    const optionsByBracket: CustomSpawnBracketValidation['metrics']['optionsByBracket'] = {
        '10+': 0,
        '3-4': 0,
        '5-6': 0,
        '7-9': 0,
    };

    for (const bracket of CUSTOM_SPAWN_BRACKETS) {
        const bracketSignatures = new Set<string>();

        for (const row of spawnData.rows) {
            const cell = row.entries[bracket];

            if (!cell || cell.none || cell.units.length === 0) {
                continue;
            }

            const signature = cell.units
                .map((unit) => `${unit.name}#${unit.count}`)
                .toSorted()
                .join('|');

            bracketSignatures.add(signature);
            signatures.add(`${bracket}:${signature}`);
        }

        optionsByBracket[bracket] = bracketSignatures.size;
    }

    const distinctSpawnOptions = signatures.size;
    const populatedBrackets = CUSTOM_SPAWN_BRACKETS.filter((bracket) => optionsByBracket[bracket] > 0).length;
    const issues: CustomSpawnBracketValidationIssue[] = [];

    for (const bracket of CUSTOM_SPAWN_BRACKETS) {
        const actual = optionsByBracket[bracket];

        if (actual < CUSTOM_SPAWN_MIN_OPTIONS_PER_BRACKET) {
            const missing = CUSTOM_SPAWN_MIN_OPTIONS_PER_BRACKET - actual;

            issues.push({
                actual,
                bracket,
                code: actual === 0 ? 'empty-bracket' : 'sparse-bracket',
                message: `${bracket} has ${actual} spawn option${actual === 1 ? '' : 's'}; add models that unlock ${missing} more.`,
                missing,
                required: CUSTOM_SPAWN_MIN_OPTIONS_PER_BRACKET,
                severity: 'error',
            });
        }
    }

    return {
        algorithmVersion: 1,
        isValid: issues.length === 0,
        issues,
        metrics: {
            distinctSpawnOptions,
            optionsByBracket,
            populatedBrackets,
            totalModels: pool.reduce((sum, entry) => sum + entry.count, 0),
        },
    };
}

/**
 * The only shape allowed across durable boundaries (RxDB, localStorage, QR/WebRTC replication and game docs).
 * It intentionally contains no raw Horde rows, 40k names, capability snapshots, or generated validation output.
 */
export const CustomSpawnBracketRecordSchema = z
    .object({
        createdAt: z.string(),
        factionIds: z.array(z.string()).default([]),
        /** Game ids currently referencing this record; permits scoped game replication without duplicating content. */
        gameIds: z.array(z.string()).default([]),
        id: z.string(),
        name: z.string().nullable(),
        pool: z.array(CustomSpawnBracketPoolEntrySchema),
        updatedAt: z.string(),
    })
    .strict();
export type CustomSpawnBracketRecord = z.infer<typeof CustomSpawnBracketRecordSchema>;

/**
 * Runtime-only projection used to render and spawn from a custom bracket. Construct this from a record with the
 * current generated faction/spawn resources; never pass it to a persistence port.
 */
export interface CustomSpawnBracket extends CustomSpawnBracketRecord {
    spawnData: FactionSpawnData;
    validation: CustomSpawnBracketValidation;
}

export const CustomSpawnBracketIndexEntrySchema = z.object({
    factionIds: z.array(z.string()),
    id: z.string(),
    modelCount: z.number(),
    name: z.string(),
    updatedAt: z.string(),
});
export type CustomSpawnBracketIndexEntry = z.infer<typeof CustomSpawnBracketIndexEntrySchema>;

export const CustomSpawnBracketIndexSchema = z.object({
    brackets: z.array(CustomSpawnBracketIndexEntrySchema),
});
export type CustomSpawnBracketIndex = z.infer<typeof CustomSpawnBracketIndexSchema>;
